/**
 * Types pour les données de l'API Apidae
 * Basé sur example_response_liste_objet_selection.json
 */

/**
 * Types de moyens de communication (enumKey)
 */
export enum MoyenCommunicationType {
  TELEPHONE = "MOYEN_COMMUNICATION_TYPE_TELEPHONE",
  MAIL = "MOYEN_COMMUNICATION_TYPE_MAIL",
  SITE_WEB = "MOYEN_COMMUNICATION_TYPE_SITE_WEB",
}

export interface LibelleMultilingue {
  libelleFr?: string;
  libelleEn?: string;
  libelleEs?: string;
  libelleIt?: string;
  libelleDe?: string;
  libelleNl?: string;
  libelleRu?: string;
  libelleZh?: string;
  libellePtBR?: string;
  libelleJa?: string;
  libelleKo?: string;
}

export interface ElementReference {
  elementReferenceType: string;
  id: number;
  enumKey?: string; // Clé enum pour identifier le type (ex: MOYEN_COMMUNICATION_TYPE_TELEPHONE)
  libelleFr: string;
  libelleEn?: string;
  libelleEs?: string;
  libelleIt?: string;
  libelleDe?: string;
  libelleNl?: string;
  libelleRu?: string;
  libelleZh?: string;
  libellePtBR?: string;
  libelleJa?: string;
  libelleKo?: string;
  ordre?: number;
  description?: string;
  familleCritere?: ElementReference;
  parent?: ElementReference;
}

export interface MoyenCommunication {
  identifiant: number;
  type: ElementReference;
  coordonnees: {
    fr?: string;
    en?: string;
    es?: string;
    it?: string;
    de?: string;
    nl?: string;
    ru?: string;
    zh?: string;
    ptBR?: string;
    ja?: string;
    ko?: string;
  };
}

export interface Commune {
  id: number;
  code: string;
  nom: string;
  pays: ElementReference;
  codePostal?: string;
}

export interface Adresse {
  adresse1?: string;
  adresse2?: string;
  adresse3?: string;
  codePostal?: string;
  commune?: Commune;
}

export interface GeoJson {
  type: string;
  coordinates: [number, number]; // [longitude, latitude]
}

export interface Geolocalisation {
  altitude?: number;
  complement?: LibelleMultilingue;
  valide: boolean;
  geoJson: GeoJson;
}

export interface Localisation {
  adresse?: Adresse;
  geolocalisation?: Geolocalisation;
}

export interface TraductionFichier {
  locale: string;
  url: string;
  urlListe: string;
  urlFiche: string;
  urlDiaporama: string;
  extension: string;
  taille: number;
  hauteur: number;
  largeur: number;
  lastModifiedDate: string;
  checksum: string;
}

export interface Illustration {
  identifiant: number;
  identifiantExterne?: string;
  link: boolean;
  type: string;
  nom: LibelleMultilingue;
  legende: LibelleMultilingue;
  copyright: LibelleMultilingue;
  traductionFichiers: TraductionFichier[];
  locked?: boolean;
  source?: string;
}

export interface Presentation {
  descriptifCourt?: LibelleMultilingue;
  descriptifDetaille?: LibelleMultilingue;
  typologiesPromoSitra?: ElementReference[];
}

export interface Informations {
  moyensCommunication?: MoyenCommunication[];
}

export interface Horaire {
  identifiant?: number;
}

export interface PeriodeOuverture {
  identifiant?: number;
  dateDebut?: string;
  dateFin?: string;
  complementHoraire?: LibelleMultilingue;
  horaireOuverture?: Horaire[];
  tousLesAns?: boolean;
}

export interface Ouverture {
  periodesOuvertures?: PeriodeOuverture[];
}

export interface ObjetTouristique {
  type: string;
  id: number;
  nom: LibelleMultilingue;
  identifier: string;
  informations?: Informations;
  presentation?: Presentation;
  localisation?: Localisation;
  illustrations?: Illustration[];
  ouverture?: Ouverture;
  informationsActivite?: {
    activiteType: ElementReference;
  };
  informationsCommerceEtService?: {
    commerceEtServiceType: ElementReference;
  };
  informationsDomaineSkiable?: {
    classification: ElementReference;
  };
  informationsFeteEtManifestation?: {
    typesManifestation: ElementReference[];
  };
  informationsEquipement?: {
    rubrique: ElementReference;
  };
  informationsHebergementCollectif?: {
    hebergementCollectifType: ElementReference;
  };
  informationsHebergementLocatif?: {
    hebergementLocatifType: ElementReference;
  };
  informationsHotelleriePleinAir?: {
    hotelleriePleinAirType: ElementReference;
  };
  informationsHotellerie?: {
    hotellerieType: ElementReference;
  };
  informationsPatrimoineCulturel?: {
    patrimoineCulturelType: ElementReference;
  };
  informationsPatrimoineNaturel?: {
    categories: ElementReference[];
  };
  informationsDegustation?: {
    typesProduit: ElementReference[];
  };
  informationsRestauration?: {
    restaurationType: ElementReference;
  };
  informationsSejourPackage?: {
    activitesCulturelles: ElementReference[];
    activitesSportives: ElementReference[];
  };
}

export interface ApidaeResponse {
  query: {
    selectionIds: number[];
    searchFields?: string;
    first: number;
    count: number;
    order?: string;
    asc?: boolean;
    projetId?: number;
  };
  numFound: number;
  facets: Map<FacetId, FacetValue[]>;
  objetsTouristiques: ObjetTouristique[];
}

/**
 * Paramètres de recherche pour l'API Apidae publique
 */
export interface ApidaeSearchParams {
  projetId: string;
  selectionIds: number[];
  responseFields?: string[];
  locales?: string[]; // liste de langues (deux lettres) (ex: ["fr", "en", "de", "it", "es", "nl", "ru", "zh", "pt-BR", "ja", "ko"])
  center?: GeoJson; // Centre de la zone de recherche. Ignoré si null ou si aucun rayon indiqué.
  radius?: number; // Rayon de la zone de recherche en mètres. Ignoré si null ou si aucun centre indiqué.
  communeCodesInsee?: string[]; // Code INSEE des communes sur lesquelles porte la recherche. Ignoré si vide ou null.
  criteresQuery?: string; // Requête au format Lucène. Ignoré si vide ou null.
  dateDebut?: string; // Date de début de période qui est comparée à la date d'ouverture. Ignoré si vide ou null. - yyyy-MM-dd
  dateFin?: string; // Date de fin de période qui est comparée à la date d'ouverture. Ignoré si vide ou null. - yyyy-MM-dd
  identifiants?: number[]; // Identifiants de référence de l'objet touristique. Ignoré si vide ou null.
  first?: number;
  count?: number;
  identifiantsIgnores?: number[]; // Identifiants de référence de l'objet touristique à ignorer. Ignoré si vide ou null.
  order?: string;
}

/**
 * Réponse de l'API Apidae pour la recherche d'objets touristiques
 */
export interface ApidaeSearchResponse {
  numFound: number;
  facets: Map<FacetId, FacetValue[]>;
  objetsTouristiques: ObjetTouristique[];
}

export interface FacetValue {
  val: string;
  label?: string;
  count?: number;
}

/**
 * Types d'identifiants des FACETS
 */
export enum FacetId {
  COMMUNE = "COMMUNE",
  ACTIVITE = "ACTIVITE",
  TYPE_CLIENTELE = "TYPE_CLIENTELE",
  SOUS_TYPE = "SOUS_TYPE",
  DISTINCTION_CLASSEMENT = "DISTINCTION_CLASSEMENT",
  DISTINCTION_LABEL = "DISTINCTION_LABEL",
  GRATUIT = "GRATUIT",
  ANIMAUX = "ANIMAUX",
  DUREE_SEANCE = "DUREE_SEANCE",
  EQU_DISTANCE_ITINERAIRE = "EQU_DISTANCE_ITINERAIRE",
}
